import React from "react";
import {
  DAYS_MAX,
  DAYS_MIN,
  DAYS_STEP,
  HOURS_MAX,
  HOURS_MIN,
  HOURS_STEP,
  buildLabel,
  toMinutes,
  validateTimeTaken,
} from "./timeEntry";

const WARNING_TEXT =
  "This job has exceeded the expected time-entry limit. Enter the actual timeframe in the note below.";

export default function TimeTakenField({ value, onChange }) {
  const current = {
    value: HOURS_MIN,
    unit: "hours",
    exceeded: false,
    note: "",
    ...value,
  };
  const isDays = current.unit === "days";
  const min = isDays ? DAYS_MIN : HOURS_MIN;
  const max = isDays ? DAYS_MAX : HOURS_MAX;
  const step = isDays ? DAYS_STEP : HOURS_STEP;
  const validation = validateTimeTaken(current);

  const emit = (next) => {
    const nextValidation = validateTimeTaken(next);
    onChange(next, nextValidation.ok);
  };

  const setUnit = (unit) => {
    emit({
      value: unit === "days" ? DAYS_MIN : HOURS_MIN,
      unit,
      exceeded: false,
      note: "",
    });
  };

  const setNumericValue = (nextValue) => {
    emit({ ...current, value: nextValue });
  };

  const stepValue = (direction) => {
    const numericValue = Number(current.value);
    const baseValue = Number.isFinite(numericValue) ? numericValue : min;
    const nextValue = Math.min(max, Math.max(min, baseValue + direction * step));
    setNumericValue(Number(nextValue.toFixed(2)));
  };

  const toggleExceeded = () => {
    const exceeded = !current.exceeded;
    emit({
      value: DAYS_MAX,
      unit: "days",
      exceeded,
      note: exceeded ? current.note : "",
    });
  };

  const previewLabel = validation.ok || current.exceeded
    ? buildLabel(current.value, current.unit, current.exceeded)
    : "—";
  const previewMinutes = Number.isFinite(Number(current.value))
    ? toMinutes(current.value, current.unit)
    : 0;

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold text-gray-800">
          Time taken <span className="text-red-500">*</span>
        </div>
        <div className="mt-1 inline-flex rounded-lg border border-gray-200 p-1">
          {["hours", "days"].map((unit) => (
            <button
              key={unit}
              type="button"
              aria-pressed={current.unit === unit}
              onClick={() => setUnit(unit)}
              className={[
                "rounded-md px-4 py-1.5 text-sm font-semibold transition",
                current.unit === unit
                  ? "bg-purple-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50",
              ].join(" ")}
            >
              {unit === "hours" ? "Hours" : "Days"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={`Decrease ${current.unit}`}
          onClick={() => stepValue(-1)}
          disabled={current.exceeded || Number(current.value) <= min}
          className="h-10 w-10 rounded-lg border border-gray-200 bg-white font-bold disabled:bg-gray-100 disabled:text-gray-400"
        >
          −
        </button>
        <input
          type="number"
          aria-label={`Time taken in ${current.unit}`}
          min={min}
          max={max}
          step={step}
          value={current.value}
          disabled={current.exceeded}
          onChange={(event) => {
            const nextValue =
              event.target.value === "" ? "" : Number(event.target.value);
            setNumericValue(nextValue);
          }}
          className="h-10 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 disabled:bg-gray-100"
        />
        <button
          type="button"
          aria-label={`Increase ${current.unit}`}
          onClick={() => stepValue(1)}
          disabled={current.exceeded || Number(current.value) >= max}
          className="h-10 w-10 rounded-lg border border-gray-200 bg-white font-bold disabled:bg-gray-100 disabled:text-gray-400"
        >
          +
        </button>
      </div>

      <button
        type="button"
        aria-pressed={current.exceeded}
        onClick={toggleExceeded}
        className={[
          "w-full rounded-lg border px-3 py-2 text-left text-sm font-semibold transition",
          current.exceeded
            ? "border-amber-400 bg-amber-50 text-amber-900"
            : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50",
        ].join(" ")}
      >
        {current.exceeded ? "✓ " : ""}99+ days
      </button>

      {current.exceeded ? (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-900">{WARNING_TEXT}</p>
          <label className="block text-sm font-semibold text-amber-950">
            Actual timeframe note <span className="text-red-600">*</span>
            <textarea
              required
              value={current.note}
              onChange={(event) =>
                emit({ ...current, note: event.target.value })
              }
              rows={3}
              className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 font-normal text-gray-900"
              placeholder="Enter the actual or best-known timeframe."
            />
          </label>
        </div>
      ) : null}

      <div className="text-sm text-gray-600" aria-live="polite">
        = <span className="font-semibold">{previewLabel}</span> ({previewMinutes} min)
      </div>

      {!validation.ok ? (
        <div className="text-sm text-red-600">{validation.error}</div>
      ) : null}
    </div>
  );
}
